import React, { useEffect, useState } from 'react';
import { Card, Typography, Space, Button, Alert, Spin } from 'antd';
import { DatabaseOutlined, ReloadOutlined } from '@ant-design/icons';
import { UserMigrationPanel } from '../components/UserMigrationPanel';
import { useUserMigration } from '../hooks/useUserMigration';

const { Title, Paragraph } = Typography;

export const UserMigrationView: React.FC = () => {
  const {
    migrationEnabled,
    statusLoading,
    refetchStatus,
  } = useUserMigration();

  const handleRefreshStatus = () => {
    refetchStatus();
  };

  if (statusLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Typography.Text>Checking migration service status...</Typography.Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={2}>
              <DatabaseOutlined /> User Migration
            </Title>
            <Paragraph>
              Import users from the legacy SACCO portal system to the new NubUser system.
              This tool preserves hashed passwords and maps legacy roles to new user groups.
            </Paragraph>
          </div>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefreshStatus}
            loading={statusLoading}
          >
            Refresh Status
          </Button>
        </Space>
      </div>

      {migrationEnabled === false && (
        <Alert
          message="Migration Service Disabled"
          description={
            <div>
              <p>The user migration service is currently disabled. To enable it:</p>
              <ol>
                <li>Set the environment variable: <code>MIGRATION_USERS_ENABLED=true</code></li>
                <li>Or add to application.properties: <code>scalefi.migration.users.enabled=true</code></li>
                <li>Restart the application</li>
                <li>Ensure you have USER_ADMIN or SYSTEM_ADMIN permissions</li>
              </ol>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <UserMigrationPanel />

      {migrationEnabled === true && (
        <Card 
          title="Migration Guidelines" 
          style={{ marginTop: 24 }}
          size="small"
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message="Data Preservation"
              description="Hashed passwords from the legacy system will be preserved. Users can log in with their existing passwords after migration."
              type="info"
              showIcon
            />
            
            <Alert
              message="Role Mapping"
              description="Legacy roles will be mapped to new user groups: ROLE_MEMBER → MEMBER_GROUP, ROLE_LOAN_OFFICER → LOAN_OFFICER_GROUP, etc."
              type="info"
              showIcon
            />
            
            <Alert
              message="Duplicate Handling (Updated)"
              description="Users with existing email addresses or user IDs will be automatically skipped (not treated as errors). This allows safe re-running of migrations."
              type="success"
              showIcon
            />
            
            <Alert
              message="Backup Recommendation"
              description="It's recommended to backup your current user database before running the migration."
              type="error"
              showIcon
            />
          </Space>
        </Card>
      )}
    </div>
  );
};
